import { Overlay, OverlayConfig, PositionStrategy} from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import { Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { WizPopUp } from '../../../components/popup/wiz-popup';
import { IWizPop } from '../../../interfaces/iwiz-pop';
import { WIZ_PHYSIC_POSITION, WizStringUtils } from '@wizeoo/akm-core-lib';

/**
 * WizToastManagerManager
 * Manager permettant d'afficher / supprimer des toast
 */
export class ToastManager {

  public static overlay:Overlay; // alimenté par le WizModule
  public static injector: Injector; // pour l'injection des datas dans le rendu du toast

  public static margin: string = '10px'; // marge
  public static gap: string = '3px'; // gap
  public static defaultTimeOut: number = 5000; // ttl par défaut
  public static defaultPosition: WIZ_PHYSIC_POSITION = WIZ_PHYSIC_POSITION.BOTTOM_RIGHT; // position par défaut
  public static defaultShowCloseButton: boolean = false;
  public static defaultTapClose: boolean = true;
  public static maxToasts: number = 6;// pas plus de 6 toasts, les nouveaux chassent les vieux

  /**
   * current Pops keys are pops'uuid
   * values are NOT instances, but WizPopUp. WizPopUp own is instance
   */
  private static currentPops: BehaviorSubject<Map<string, WizPopUp>> = new BehaviorSubject(new Map());

  /** la pile des currentPops, en PILE, pour gestion du FIFO sur dépassement */
  private static fifoPile: WizPopUp[] = [];


  /**
   *  closing pop, unregister
   * @param pop : WizPopUp to kill
   * @param byPassControl pour traitement interne en oneShot du hideAll
   */
  private static unregisterPop(pop: WizPopUp, byPassControl: boolean = false) {
    ToastManager.currentPops.getValue().delete(pop.uuid); // delete from currentPops' pile
    ToastManager.currentPops.next(ToastManager.currentPops.getValue()); // update pile
    this.fifoPile.splice(this.fifoPile.indexOf(pop),1); // nettoyage de la pile

    if(!byPassControl) {// pas de contrôle en fermeture All
      this.checkOverflow();
      this.draw(); // on redessine
    }
  }

  /**
   * on showing a new pop, register
   * @param pop : WizPopUp to add to currentPops
   */
  private static registerPop(pop: WizPopUp) {
    ToastManager.currentPops.next(ToastManager.currentPops.getValue().set(pop.uuid, pop));
    this.fifoPile.push(pop);// pile pour suppression sur excès de notifs
    this.checkOverflow();
    this.draw(); // on redessine
  }

  /** contrôle de la taille de la pile et suppression éventuelle de l'excédent */
  private static checkOverflow(): void {
    if(this.fifoPile.length> this.maxToasts) {
      const toastToDelete: WizPopUp = this.fifoPile.slice(0, 1)[0];
      this.hide(toastToDelete);
    }
  }

  /**
   * want to show a pop (WizPopUp.show())
   * @param pop: WizPopUp
   */
  public static show( pop: WizPopUp): WizPopUp {
    // console.log('show toast', pop.contentText);

    const positionStrategy = this.getPositionStrategy(this.defaultPosition,this.margin); // orientation
    const overlayConf = new OverlayConfig();
    overlayConf.positionStrategy = positionStrategy;

    pop.toastOverlay = ToastManager.overlay.create(overlayConf);// création de la couche container
    const tokens = new WeakMap();// le token est nécessaire pour passer les données à l'injecteur
    tokens.set(WizPopUp, pop);
    const injector = new PortalInjector(ToastManager.injector, tokens);
    const toast: ComponentPortal<IWizPop> = new ComponentPortal(pop.classFactory, null, injector);// creation du compo par la factory

    pop.toastOverlay.attach(toast); // association au container
    ToastManager.registerPop(pop); // enregistrement pour suppression ultérieure

    return pop;
  }

  /**
   * want to show a pop (WizPopUp.show())
   * @param pop: WizPopUp
   */
  public static hide( pop: WizPopUp): WizPopUp {
    // console.log('hide toast', pop.contentText);
    this.unregisterPop(pop); // suppression de la pile de pops
    pop.toastOverlay.dispose();
    return pop;
  }

  /**
   * fermeture forcée de tous les toasts
   */
  public static hideAll(): void {
    // console.log('hide toast', pop.contentText);
    [...this.fifoPile].forEach((item:WizPopUp)=> {
      this.unregisterPop(item, true); // suppression de la pile de pops
      item.toastOverlay.dispose();
    });
  }

  /**
   * Convert WIZ_PHYSIC_POSITION to positionStrategy
   * @param position : WIZ_PHYSIC_POSITION
   * @param coord: les coordonnées de la partie "dynamique" de la position, formatée string (120px)
   * @return PositionStrategy
   */
  private static getPositionStrategy(position: WIZ_PHYSIC_POSITION, coord:string): PositionStrategy {
    let positionStrategy: PositionStrategy; // objet retour

    switch (position) {
      case WIZ_PHYSIC_POSITION.TOP_LEFT:
        positionStrategy = this.overlay.position()
          .global()
          .top(coord)
          .left(this.margin);
        break;

      case WIZ_PHYSIC_POSITION.TOP_RIGHT:
        positionStrategy = this.overlay.position()
          .global()
          .top(coord)
          .right(this.margin);
        break;
      case WIZ_PHYSIC_POSITION.BOTTOM_LEFT:
        positionStrategy = this.overlay.position()
          .global()
          .bottom(coord)
          .left(this.margin);
        break;
      case WIZ_PHYSIC_POSITION.MIDDLE_CENTER:
      case WIZ_PHYSIC_POSITION.BOTTOM_RIGHT:
        positionStrategy = this.overlay.position()
          .global()
          .bottom(coord)
          .right(this.margin);

        if (position === WIZ_PHYSIC_POSITION.MIDDLE_CENTER) {
          console.warn('no toast on the middle of the screen, you, donkey...');
        }
        break;
    }
    return positionStrategy;
  }

  /**
   * repositionne tous les toasts sur changement(show / hide)
   */
  private static draw(): void {

    // la pile de travail (clone de la pile pour ne pas propager l'éventuel reverse)
    const workingPile: WizPopUp[] = [...this.fifoPile];
    if (this.defaultPosition === WIZ_PHYSIC_POSITION.BOTTOM_RIGHT || this.defaultPosition === WIZ_PHYSIC_POSITION.BOTTOM_LEFT) {
      // si on part du base, reverse
      workingPile.reverse();
    }

    const gap: number = WizStringUtils.measureToNumber(this.gap); // gap
    let from: number = WizStringUtils.measureToNumber(this.margin); // coordonnées initiales (y)

    workingPile.forEach((item: WizPopUp) => { // on dépile
      // console.log('draw', item .contentText);
      // const newPos = ToastManager.overlay.position().global().bottom(from+'px').right(this.margin);
      const newPos =this.getPositionStrategy(this.defaultPosition, from+'px' );

      item.toastOverlay.updatePositionStrategy(newPos); // maj du toast
      item.toastOverlay.updatePosition();

      const height:number = WizStringUtils.measureToNumber(item.height);
      from = from + height + gap ; // nouvelles coordonnées
    });
  }

}
